import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ANONYMITY_THRESHOLD,
  type AnonymousResults,
  type SurveyDetail,
  type SurveyQuestionInput,
  type SurveyResponseMode,
  type SurveyResponses,
  type SurveySubmission,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Select,
  Tab,
  Tabs,
  TabsList,
  TabsPanel,
} from '../../components/ui'
import QuestionBuilder from '../../components/survey/QuestionBuilder'
import QuestionField from '../../components/survey/QuestionField'
import { describeShowIf } from '../../components/survey/conditionText'
import AnswerList from '../../components/survey/AnswerList'
import PublicLinkPanel from '../../components/survey/PublicLinkPanel'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/dates'

export default function OperatorSurvey() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { workspaceId, surveyId } = useParams<{ workspaceId: string; surveyId: string }>()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'

  const [survey, setSurvey] = useState<SurveyDetail | null>(null)
  const [submission, setSubmission] = useState<SurveySubmission | null>(null)
  const [responses, setResponses] = useState<SurveyResponses | null>(null)
  const [responseMode, setResponseMode] = useState<SurveyResponseMode>('collective')
  const [anonymous, setAnonymous] = useState(false)
  const [allowsPublic, setAllowsPublic] = useState(false)
  const [draft, setDraft] = useState<SurveyQuestionInput[]>([])
  const [title, setTitle] = useState('')
  const [dueAt, setDueAt] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const base = `/api/workspaces/${workspaceId}/surveys/${surveyId}`

  const load = useCallback(async () => {
    const response = await api.get<{ data: SurveyDetail }>(base)
    setSurvey(response.data)
    setTitle(response.data.title)
    setDueAt(response.data.due_at?.slice(0, 10) ?? '')
    setResponseMode(response.data.response_mode)
    setAnonymous(response.data.anonymous)
    setAllowsPublic(response.data.allows_public)
    setDraft(
      response.data.questions.map((question) => ({
        code: question.code,
        type: question.type,
        label: question.label,
        description: question.description,
        required: question.required,
        config: question.config,
      })),
    )

    if (response.data.status !== 'draft') {
      // One collective answer, or one per person: two shapes, two endpoints.
      if (response.data.response_mode === 'individual') {
        const list = await api.get<{ data: SurveyResponses }>(`${base}/responses`).catch(() => null)
        setResponses(list?.data ?? null)
      } else {
        const answers = await api
          .get<{ data: SurveySubmission }>(`${base}/submission`)
          .catch(() => null)
        setSubmission(answers?.data ?? null)
      }
    }
  }, [base])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const save = async () => {
    setSaving(true)
    setError(null)

    try {
      await api.put(base, {
        title,
        due_at: dueAt || null,
        ...(survey?.status === 'draft'
          ? {
              questions: draft,
              response_mode: responseMode,
              anonymous,
              // Only an admin decides this, and only the admin's request carries it.
              ...(isAdmin ? { allows_public: allowsPublic } : {}),
            }
          : {}),
      })
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  const act = async (action: 'publish' | 'close' | 'reopen') => {
    setError(null)
    try {
      await api.post(`${base}/${action}`, {})
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const remove = async () => {
    try {
      await api.delete(base)
      navigate(`/app/workspaces/${workspaceId}`)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  if (loading) return <SectionSpinner />
  if (!survey) return <EmptyState title={t('errors.notFound')} description={error ?? undefined} />

  const isDraft = survey.status === 'draft'

  return (
    <>
      <PageHeader
        backTo={{ href: `/app/workspaces/${workspaceId}`, label: t('workspaces.backToWorkspace') }}
        title={survey.title}
        subtitle={
          survey.template_version
            ? `${t(`surveys.status_${survey.status}`)} · ${t('surveys.masterVersion', { version: survey.template_version })}`
            : t(`surveys.status_${survey.status}`)
        }
        actions={
          <div className="flex gap-2">
            {isDraft && (
              <>
                <ConfirmDialog
                  title={t('surveys.deleteTitle')}
                  description={t('surveys.deleteConfirm')}
                  onConfirm={remove}
                  trigger={
                    <Button variant="secondary">{t('common.delete')}</Button>
                  }
                />
                <Button onClick={() => act('publish')} disabled={draft.length === 0}>
                  {t('surveys.publish')}
                </Button>
              </>
            )}
            {survey.status === 'published' && survey.anonymous && (
              // Final for an anonymous survey: the answers are released once, at
              // close, and never a second time. Below the threshold they are
              // never shown at all, so the dialog says how many there are.
              <ConfirmDialog
                title={t('surveys.anonymousCloseTitle')}
                description={t('surveys.anonymousCloseConfirm', {
                  given: responses?.anonymous?.count ?? 0,
                  threshold: ANONYMITY_THRESHOLD,
                })}
                confirmLabel={t('surveys.close')}
                onConfirm={() => act('close')}
                trigger={<Button variant="secondary">{t('surveys.close')}</Button>}
              />
            )}
            {survey.status === 'published' && !survey.anonymous && (
              <Button variant="secondary" onClick={() => act('close')}>
                {t('surveys.close')}
              </Button>
            )}
            {survey.status === 'closed' && !survey.anonymous && (
              <Button variant="secondary" onClick={() => act('reopen')}>
                {t('surveys.reopen')}
              </Button>
            )}
          </div>
        }
      />

      {error && <Alert className="mb-6">{error}</Alert>}

      {!isDraft && (
        <Alert variant="info" className="mb-6">
          {t('surveys.frozenNote')}
        </Alert>
      )}

      {isDraft &&
        survey.anonymous &&
        survey.respondent_total !== undefined &&
        survey.respondent_total < ANONYMITY_THRESHOLD && (
          <Alert variant="warning" className="mb-6">
            {t('surveys.anonymousTooSmall', {
              total: survey.respondent_total,
              threshold: ANONYMITY_THRESHOLD,
            })}
          </Alert>
        )}

      {survey.anonymous && survey.status === 'closed' && (
        <Alert variant="info" className="mb-6">
          {t('surveys.anonymousClosedNote')}
        </Alert>
      )}

      <Tabs defaultValue={isDraft ? 'build' : 'answers'}>
        <TabsList className="mb-6 w-full">
          <Tab value="build">{t('surveys.tabs.build')}</Tab>
          {!isDraft && <Tab value="answers">{t('surveys.tabs.answers')}</Tab>}
          {!isDraft && survey.allows_public && (
            <Tab value="public">{t('surveys.tabs.public')}</Tab>
          )}
        </TabsList>

        <TabsPanel value="build">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <Card className="space-y-4 p-6">
                <Field label={t('surveys.fields.title')} required>
                  <Input value={title} onChange={(event) => setTitle(event.target.value)} />
                </Field>
                <Field label={t('surveys.responseMode')} hint={t('surveys.responseModeHint')}>
                  <Select
                    value={responseMode}
                    // A survey that allows a public link is one answer per person: an
                    // operator who cannot take the link off cannot change this either.
                    disabled={!isDraft || (allowsPublic && !isAdmin)}
                    onChange={(event) => {
                      const next = event.target.value as SurveyResponseMode
                      setResponseMode(next)
                      if (next !== 'individual') {
                        setAnonymous(false)
                        setAllowsPublic(false)
                      }
                    }}
                    options={[
                      { value: 'collective', label: t('surveys.mode_collective') },
                      { value: 'individual', label: t('surveys.mode_individual') },
                    ]}
                  />
                </Field>
                {responseMode === 'individual' && (
                  <label className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={anonymous}
                      disabled={!isDraft}
                      onChange={(event) => {
                        setAnonymous(event.target.checked)
                        // A file is stored with whoever uploaded it: an anonymous
                        // survey cannot keep one, so the question goes with the flag.
                        if (event.target.checked) {
                          setDraft((questions) => questions.filter((q) => q.type !== 'file'))
                        }
                      }}
                      className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-900 dark:text-gray-50">
                        {t('surveys.anonymousLabel')}
                      </span>
                      <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                        {t('surveys.anonymousHint')}
                      </span>
                    </span>
                  </label>
                )}
                {responseMode === 'individual' && (isAdmin || allowsPublic) && (
                  <label className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      checked={allowsPublic}
                      disabled={!isDraft || !isAdmin}
                      onChange={(event) => {
                        setAllowsPublic(event.target.checked)
                        // An attachment from anyone on the internet would land in the
                        // storage: the question goes with the flag, as for anonymity.
                        if (event.target.checked) {
                          setDraft((questions) => questions.filter((q) => q.type !== 'file'))
                        }
                      }}
                      className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block font-medium text-gray-900 dark:text-gray-50">
                        {t('surveys.allowsPublicLabel')}
                      </span>
                      <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                        {t('surveys.allowsPublicHint')}
                      </span>
                    </span>
                  </label>
                )}
                <Field label={t('surveys.fields.dueAt')} hint={t('surveys.fields.dueAtHint')}>
                  <Input
                    type="date"
                    value={dueAt}
                    onChange={(event) => setDueAt(event.target.value)}
                  />
                </Field>
              </Card>

              {isDraft && (
                <QuestionBuilder
                  questions={draft}
                  onChange={setDraft}
                  excludeFile={anonymous || allowsPublic}
                />
              )}

              <div className="flex justify-end">
                <Button onClick={save} loading={saving}>
                  {t('common.save')}
                </Button>
              </div>
            </div>

            <Card className="h-fit min-w-0 p-6">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-gray-50">
                {t('surveys.preview')}
              </h3>
              {survey.questions.length === 0 ? (
                <EmptyState title={t('surveys.previewEmpty')} />
              ) : (
                <div className="space-y-6">
                  {survey.questions.map((question) => (
                    <div key={question.id}>
                      {question.config?.showIf && (
                        // The preview shows every question, so the rule is written out: it is
                        // the one place an operator reads how the survey will branch.
                        <p className="mb-1 text-xs font-medium text-primary-700 dark:text-primary-400">
                          {t('surveys.condition.shownOnlyIf', {
                            rule: describeShowIf(question.config.showIf, survey.questions, t),
                          })}
                        </p>
                      )}
                      <QuestionField
                        question={question}
                        value={null}
                        onChange={() => undefined}
                        disabled
                      />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsPanel>

        {!isDraft && survey.allows_public && (
          <TabsPanel value="public">
            <PublicLinkPanel
              workspaceId={workspaceId!}
              survey={survey}
              isAdmin={isAdmin}
              locale={i18n.language}
            />
          </TabsPanel>
        )}

        {!isDraft && (
          <TabsPanel value="answers">
            {survey.response_mode === 'individual' ? (
              <RespondentsPanel survey={survey} responses={responses} locale={i18n.language} />
            ) : (
              <AnswersPanel survey={survey} submission={submission} locale={i18n.language} />
            )}
          </TabsPanel>
        )}
      </Tabs>
    </>
  )
}

function AnswersPanel({
  survey,
  submission,
  locale,
}: {
  survey: SurveyDetail
  submission: SurveySubmission | null
  locale: string
}) {
  const { t } = useTranslation()

  if (!submission || submission.answers.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('surveys.noAnswersTitle')}
          description={t('surveys.noAnswersDescription')}
        />
      </Card>
    )
  }

  return (
    <Card className="divide-y divide-gray-100 dark:divide-gray-800 p-0">
      <div className="flex items-center justify-between gap-3 p-4">
        <Badge variant={submission.status === 'submitted' ? 'success' : 'warning'}>
          {t(`surveys.submission_${submission.status}`)}
        </Badge>
        {submission.submitted_at && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDateTime(submission.submitted_at, locale)}
          </span>
        )}
      </div>

      <AnswerList survey={survey} answers={submission.answers} locale={locale} />
    </Card>
  )
}

/**
 * The answers of an anonymous survey: each one on its own, with nothing to say
 * who wrote it, and withheld until they can be shown without pointing at
 * anyone. The page says why when they are not there yet, and what to do about
 * a workspace too small for the threshold to ever be reached.
 */
function AnonymousResultsPanel({
  survey,
  results,
  locale,
}: {
  survey: SurveyDetail
  results: AnonymousResults
  locale: string
}) {
  const { t } = useTranslation()

  if (!results.released) {
    return (
      <Alert variant="info">
        {t('surveys.anonymousWithheld', { given: results.count, threshold: results.threshold })}
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
        {t('surveys.anonymousResultsTitle', { total: results.count })}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('surveys.anonymousResultsNote')}
      </p>
      {results.submissions.map((submission, index) => (
        <Card key={index} className="divide-y divide-gray-100 p-0 dark:divide-gray-800">
          <p className="p-4 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t('surveys.anonymousResponse', { n: index + 1 })}
          </p>
          <AnswerList
            survey={survey}
            answers={submission.answers.map((answer) => ({
              ...answer,
              answered_by: null,
              answered_at: '',
            }))}
            locale={locale}
          />
        </Card>
      ))}
    </div>
  )
}

/**
 * An individual survey: how far along it is, then each person with where they
 * stand. The answers of someone who has not started are not shown because
 * there are none; a person who has is one click from theirs.
 */
function RespondentsPanel({
  survey,
  responses,
  locale,
}: {
  survey: SurveyDetail
  responses: SurveyResponses | null
  locale: string
}) {
  const { t } = useTranslation()

  if (!responses || responses.respondents.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('surveys.noRespondentsTitle')}
          description={t('surveys.noRespondentsDescription')}
        />
      </Card>
    )
  }

  const { progress, respondents } = responses
  const percent = progress.total > 0 ? Math.round((progress.submitted / progress.total) * 100) : 0

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
          {t('surveys.respondentProgress', {
            submitted: progress.submitted,
            total: progress.total,
          })}
        </p>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={progress.total}
          aria-valuenow={progress.submitted}
          className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800"
        >
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${percent}%` }} />
        </div>
      </Card>

      {responses.anonymous && progress.total < responses.anonymous.threshold && (
        <Alert variant="warning">
          {t('surveys.anonymousTooSmall', {
            total: progress.total,
            threshold: responses.anonymous.threshold,
          })}
        </Alert>
      )}

      {responses.anonymous && (
        <AnonymousResultsPanel survey={survey} results={responses.anonymous} locale={locale} />
      )}

      <Card className="divide-y divide-gray-100 p-0 dark:divide-gray-800">
        <h3 className="p-4 text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t(responses.anonymous ? 'surveys.participationTitle' : 'surveys.respondentsTitle')}
        </h3>
        {respondents.map((respondent) => (
          <details key={respondent.respondent_id} className="group">
            <summary className="flex cursor-pointer items-center justify-between gap-3 p-4">
              <span className="text-sm text-gray-900 dark:text-gray-50">
                {respondent.respondent_name}
              </span>
              <span className="flex items-center gap-3">
                {respondent.submitted_at && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(respondent.submitted_at, locale)}
                  </span>
                )}
                <Badge
                  variant={
                    respondent.status === 'submitted'
                      ? 'success'
                      : respondent.status === 'in_progress'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {t(
                    respondent.status === 'not_started'
                      ? 'surveys.respondent_not_started'
                      : `surveys.submission_${respondent.status}`,
                  )}
                </Badge>
              </span>
            </summary>
            {respondent.answers.length > 0 && (
              <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-gray-800 dark:border-gray-800">
                <AnswerList survey={survey} answers={respondent.answers} locale={locale} />
              </div>
            )}
          </details>
        ))}
      </Card>
    </div>
  )
}
